import React, { useState } from 'react';
import { ChevronDownIcon, PlusIcon, XIcon, SearchIcon } from 'lucide-react';
import { ethers } from 'ethers';
import { getAllTokens, addCustomToken } from '../config/constantsV2';
import { ERC20_ABI } from '../contracts/abisV2';
import web3Provider from '../utils/web3Provider';
import { TokenLogos } from '../assets/tokenLogos';

const TokenSelectorV2 = ({ 
  selectedToken, 
  onTokenSelect, 
  excludeToken = null,
  label = "Select Token",
  disabled = false 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customAddress, setCustomAddress] = useState('');
  const [loadingCustom, setLoadingCustom] = useState(false);
  const [customError, setCustomError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const allTokens = getAllTokens();
  
  const filteredTokens = allTokens.filter(token => {
    const matchesSearch = !searchQuery || 
      token.symbol.toLowerCase().includes(searchQuery.toLowerCase()) ||
      token.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      token.address.toLowerCase().includes(searchQuery.toLowerCase());
    
    const notExcluded = !excludeToken || token.address !== excludeToken.address;
    
    return matchesSearch && notExcluded;
  });

  const handleTokenSelect = (token) => {
    onTokenSelect(token);
    setIsOpen(false);
    setSearchQuery('');
    setShowCustomInput(false);
  };

  const loadCustomToken = async () => {
    if (!customAddress || !ethers.isAddress(customAddress)) {
      setCustomError('Invalid address');
      return;
    }

    setLoadingCustom(true);
    setCustomError(null);

    try {
      const provider = web3Provider.getProvider();
      if (!provider) {
        throw new Error('Please connect wallet first');
      }

      const tokenContract = new ethers.Contract(customAddress, ERC20_ABI, provider);

      // Fetch token details
      const [symbol, name, decimals] = await Promise.all([
        tokenContract.symbol(),
        tokenContract.name(),
        tokenContract.decimals(),
      ]);

      const customTokenData = {
        address: customAddress,
        symbol: symbol,
        name: name,
        decimals: Number(decimals),
        logo: TokenLogos.GIGACHAD, // Default logo for custom tokens
        isNative: false,
        isCustom: true,
        isWhitelisted: false,
      };

      // Add to custom tokens
      addCustomToken(customTokenData);

      // Select the token
      handleTokenSelect(customTokenData);
      setCustomAddress('');
      setShowCustomInput(false);
    } catch (error) {
      console.error('Error loading custom token:', error);
      setCustomError('Failed to load token. Make sure it\'s a valid ERC20 token.');
    } finally {
      setLoadingCustom(false);
    }
  };

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-white/80 mb-2">
        {label}
      </label>
      
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`
          w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl
          backdrop-blur-sm text-white text-left flex items-center justify-between
          transition-all duration-200 hover:bg-white/10 hover:border-white/20
          focus:outline-none focus:ring-2 focus:ring-blue-500/50
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        `}
      >
        {selectedToken ? (
          <div className="flex items-center space-x-3">
            <img 
              src={selectedToken.logo} 
              alt={selectedToken.symbol}
              className="w-8 h-8 rounded-full object-contain bg-white/10 p-1"
            />
            <div>
              <div className="font-semibold flex items-center gap-2">
                {selectedToken.symbol}
                {selectedToken.isCustom && (
                  <span className="text-xs bg-yellow-500/20 text-yellow-300 px-2 py-0.5 rounded">
                    Custom
                  </span>
                )}
              </div>
              <div className="text-sm text-white/60">{selectedToken.name}</div>
            </div>
          </div>
        ) : (
          <span className="text-white/60">Select a token</span>
        )}
        
        <ChevronDownIcon 
          className={`w-5 h-5 text-white/60 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-gray-900/95 backdrop-blur-xl border border-white/20 rounded-xl shadow-2xl z-50 max-h-96 overflow-hidden flex flex-col">
          {/* Search bar */}
          <div className="p-3 border-b border-white/10">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/40" />
              <input
                type="text"
                placeholder="Search by name, symbol, or address..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              />
            </div>
          </div>

          {/* Token list */}
          <div className="overflow-y-auto max-h-60">
            {filteredTokens.length > 0 ? (
              filteredTokens.map((token) => (
                <button
                  key={token.address}
                  onClick={() => handleTokenSelect(token)}
                  className="w-full px-4 py-3 text-left hover:bg-white/15 transition-colors duration-150 flex items-center justify-between"
                >
                  <div className="flex items-center space-x-3">
                    <img 
                      src={token.logo} 
                      alt={token.symbol}
                      className="w-8 h-8 rounded-full object-contain bg-white/10 p-1"
                    />
                    <div>
                      <div className="font-semibold text-white flex items-center gap-2">
                        {token.symbol}
                        {token.isCustom && (
                          <span className="text-xs bg-yellow-500/20 text-yellow-300 px-2 py-0.5 rounded">
                            Custom
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-white/60">{token.name}</div>
                    </div>
                  </div>
                </button>
              ))
            ) : (
              <div className="px-4 py-6 text-center text-white/40">
                No tokens found
              </div>
            )}
          </div>

          {/* Add custom token */}
          <div className="border-t border-white/10 p-3">
            {!showCustomInput ? (
              <button
                onClick={() => setShowCustomInput(true)}
                className="w-full px-4 py-2 bg-blue-500/20 hover:bg-blue-500/30 text-blue-300 rounded-lg flex items-center justify-center gap-2 transition-colors"
              >
                <PlusIcon className="w-4 h-4" />
                Add Custom Token
              </button>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    placeholder="Token contract address (0x...)"
                    value={customAddress}
                    onChange={(e) => {
                      setCustomAddress(e.target.value);
                      setCustomError(null);
                    }}
                    className="flex-1 px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/40 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  />
                  <button
                    onClick={() => {
                      setShowCustomInput(false);
                      setCustomAddress('');
                      setCustomError(null);
                    }}
                    className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                  >
                    <XIcon className="w-4 h-4 text-white/60" />
                  </button>
                </div>
                
                {customError && (
                  <div className="text-xs text-red-400">{customError}</div>
                )}
                
                <button
                  onClick={loadCustomToken}
                  disabled={loadingCustom || !customAddress}
                  className="w-full px-4 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-blue-500/50 disabled:cursor-not-allowed text-white rounded-lg text-sm transition-colors"
                >
                  {loadingCustom ? 'Loading...' : 'Add Token'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TokenSelectorV2;
